import type { ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useExtracted } from "next-intl";
import { FoilButton } from "@/components/ui/foil-button";

interface RecoveryFormProps {
  challenge?: ReactNode;
  error?: string;
  email: string;
  onEmailChange: (v: string) => void;
  sent: boolean;
  loading: boolean;
  onSubmit: () => void;
  onBack: () => void;
}

export function RecoveryForm({
  challenge,
  error,
  email,
  onEmailChange,
  sent,
  loading,
  onSubmit,
  onBack,
}: RecoveryFormProps) {
  const te = useExtracted();

  return (
    <motion.div
      key="recover"
      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      className="flex w-full max-w-70 flex-col items-center gap-3"
      exit={{
        opacity: 0,
        y: -8,
        filter: "blur(2px)",
        transition: { duration: 0.15, ease: "easeIn" },
      }}
      initial={{ opacity: 0, y: 12, filter: "blur(2px)" }}
      transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1], delay: 0.05 }}
    >
      <AnimatePresence mode="wait">
        {sent ? (
          <motion.p
            key="sent"
            animate={{ opacity: 1, y: 0 }}
            className="text-profit text-center text-base"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {te(
              "If an account exists with that email, your PIN has been sent.",
            )}{" "}
            <span className="text-faint text-sm">
              {te("Don't see it? Check your spam folder.")}
            </span>
          </motion.p>
        ) : (
          <motion.div
            key="form"
            animate={{ opacity: 1, y: 0 }}
            className="flex w-full flex-col items-center gap-3"
            exit={{ opacity: 0, y: -4 }}
            initial={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            <input
              autoFocus
              className="h-12 w-full rounded-[17px] border border-white/10 bg-black/24 px-4 text-sm text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.055),0_10px_28px_rgba(0,0,0,0.18)] backdrop-blur-xl transition-[border-color,background-color,box-shadow] duration-300 outline-none placeholder:text-white/32 hover:border-white/16 focus:border-cyan-100/28 focus:bg-white/[0.035] focus:shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_0_0_3px_rgba(143,232,244,0.045),0_12px_32px_rgba(0,0,0,0.2)]"
              placeholder={te("Email used at purchase")}
              type="email"
              value={email}
              onChange={(e) => onEmailChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && email) onSubmit();
              }}
            />
            <div className="w-full rounded-[17px] bg-[linear-gradient(100deg,rgba(143,232,244,0.58),rgba(174,188,248,0.26)_52%,rgba(191,116,242,0.58))] p-px shadow-[0_10px_30px_rgba(150,190,255,0.08)]">
              <FoilButton
                className="h-[46px] w-full rounded-[16px] text-sm tracking-wide backdrop-blur-xl transition-[filter] hover:brightness-110"
                disabled={loading || !email}
                size="lg"
                tone="aurora"
                onClick={onSubmit}
              >
                {loading ? "..." : te("Send PIN")}
              </FoilButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {challenge}
      {error && (
        <p role="alert" className="text-loss text-center text-sm">
          {error}
        </p>
      )}
      <button
        className="text-muted-foreground hover:text-foreground cursor-pointer text-base transition-colors"
        onClick={onBack}
      >
        {te("Back to login")}
      </button>
    </motion.div>
  );
}
